import React, { useEffect, useContext, useState } from 'react';
import { TooltipFormatterContextObject } from 'highcharts';
import { ChartContainer, TableHeading } from './CiTradesChart.style';
import { ciTradeChartData } from '../../../../../../api/services';
import {
  groupAndSortDataByParameter,
  generateSeries,
  getChartXAxisCategories,
  IRegionQuarterData,
  tooltipFormatter,
} from './CiTradesUtils';
import Chart from '../../../Chart/Chart';
import { Flexbox } from '@sede-enterprise/shell-ds-react-framework';
import { AppContext } from '../../../../../../Context/AppContext';
import { ICiTradeChartData } from '../../../../../../Types/types';
import Loader from '../../../../../Loader/Loader';
import { errorNotifyMessage, isApiResponseEmpty } from '../../../../../Utils/utils';
import { notify } from '../../../../../../../CarbonDashboard/components/Toast/notify';

interface CiTradesChartProps {
  setIsCiTradeChartApiResponseEmpty: (isEmpty: boolean) => void;
}

const CiTradesChart: React.FC<CiTradesChartProps> = ({ setIsCiTradeChartApiResponseEmpty }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { state: analyticsState, dispatch: analyticsDispatch } =
    useContext(AppContext).ciTradeAndVolume;

  const mapData: ICiTradeChartData[] = analyticsState.ciTradeChartData;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      let response;
      try {
        response = await ciTradeChartData(analyticsState.ciTradeFilteredValues);

        // Use the analyticsDispatch function to call setCiTradeChartData
        analyticsDispatch({
          type: 'setCiTradeChartData',
          payload: response.data,
        });
        if (analyticsState.ciTradeFilteredValues.region.length !== 0 &&
          analyticsState.ciTradeFilteredValues.commodity.length !== 0) {
          setIsCiTradeChartApiResponseEmpty(isApiResponseEmpty(
            response.data,
            analyticsState.ciTradeFilteredValues.region,
            analyticsState.ciTradeFilteredValues.commodity
          ));
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
         notify('error', errorNotifyMessage(error));
      }
    };

    fetchData();
  }, [analyticsDispatch, analyticsState.ciTradeFilteredValues, setIsCiTradeChartApiResponseEmpty]); // Add all dependencies

  // Group and sort data by region
  const dataByRegion = groupAndSortDataByParameter(mapData);
  const series = generateSeries(dataByRegion);
  const titleText = 'Year';

  const options = {
    chart: {
      type: 'column',
    },
    xAxis: {
      categories: getChartXAxisCategories(dataByRegion as Record<string, IRegionQuarterData[]>),
      crosshair: true,
      title: {
        text: titleText,
      },
    },
    yAxis: {
      title: {
        text: `Deal Quantity`,
      },
      minRange: 0.1,
    },
    tooltip: {
      shared: false,
      // eslint-disable-next-line no-unused-vars
      formatter(this: TooltipFormatterContextObject) {
        return tooltipFormatter(this);
      },
    },
    plotOptions: {
      series: {
        pointWidth: 20,
        pointPadding: 0.2, // Padding between each column or bar, defaults to 0.1
        groupPadding: 0.2, // Padding between each value groups, in x axis units, defaults to 0.2
        dataLabels: {
          enabled: true,
          format: '{point.y:.3f}', // upto 3 decimal points
        },
      },
    },
    series,
  };

  return (
    <ChartContainer data-testid='map-container'>
      <Loader loader={isLoading} message={''} />
      <Flexbox style={{ width: '100%' }} justifyContent='space-between'>
        <TableHeading>CI Trades Chart</TableHeading>
      </Flexbox>
      <Chart options={options} footer={'Source'} />
      <div style={{ display: 'none' }} data-testid='analytics-nci-chart-hidden-for-automation'>
        {JSON.stringify(options.series)}
      </div>
    </ChartContainer>
  );
};

export default CiTradesChart;